import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaKey, FaPlus, FaChevronRight, FaCloudUploadAlt } from 'react-icons/fa';
import { uploadFile } from '../../upload-file';
import { Page, PageHeader, Card, SearchInput, Switch, Modal, Field, EmptyRow, Alert, Loading, initials } from '../../ui';
import { api } from '../../ui/api';

const EMPTY = {
  companyName: '', companyEmail: '', companyPhone: '',
  representativeName: '', representativePhone: '', password: '', documents: [],
};

const ManageVendors = () => {
  const [vendors, setVendors] = useState(null);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState(null); // 'add' | 'edit' | 'password'
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setVendors(await api('/vendor/all_vendors'));
    } catch (e) {
      setLoadError(e.message);
      setVendors([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (nextMode, vendor = null) => {
    setCurrent(vendor);
    setError('');
    setProgress(0);
    setForm(vendor ? {
      ...EMPTY,
      companyName: vendor.company_name, companyEmail: vendor.company_email, companyPhone: vendor.company_phone,
      representativeName: vendor.representative_name, representativePhone: vendor.representative_phone,
    } : EMPTY);
    setMode(nextMode);
  };
  const close = () => setMode(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (mode === 'add') {
        const urls = await Promise.all(form.documents.map((file) => uploadFile(file, setProgress, 'vendor-docs')));
        await api('/vendor/add_vendor', {
          method: 'POST',
          body: {
            company_name: form.companyName, company_email: form.companyEmail, company_phone: form.companyPhone,
            representative_name: form.representativeName, representative_phone: form.representativePhone,
            documents: urls, password: form.password,
          },
        });
      } else if (mode === 'edit') {
        const { companyName, companyEmail, companyPhone, representativeName, representativePhone } = form;
        await api(`/vendor/edit_vendor/${current.id}`, {
          method: 'PUT',
          body: { companyName, companyEmail, companyPhone, representativeName, representativePhone },
        });
      } else {
        await api(`/vendor/change_password/${current.id}`, { method: 'PUT', body: { newPassword: form.password } });
      }
      close();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (vendor) => {
    const verb = vendor.is_active ? 'Deactivate' : 'Activate';
    const note = vendor.is_active ? ' They will no longer be able to sign in.' : '';
    if (!window.confirm(`${verb} ${vendor.company_name}?${note}`)) return;
    try {
      await api(`/vendor/toggle_active/${vendor.id}`, { method: 'PATCH', body: { is_active: !vendor.is_active } });
      load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const q = query.toLowerCase();
  const rows = (vendors || []).filter((v) =>
    v.company_name.toLowerCase().includes(q) || v.representative_name.toLowerCase().includes(q)
    || (v.company_email || '').toLowerCase().includes(q));

  const titles = { add: 'Add vendor', edit: 'Edit vendor', password: 'Reset password' };

  return (
    <Page>
      <PageHeader
        title="Vendors"
        subtitle="Partner companies that take on audits and jobs."
        actions={<button className="btn btn-primary" onClick={() => open('add')}><FaPlus /> Add vendor</button>}
      />
      <Alert>{loadError}</Alert>
      <Card flush>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by company, contact or email" />
          {vendors && <span className="count">{rows.length} of {vendors.length}</span>}
        </div>
        {!vendors ? <Loading /> : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact person</th>
                  <th>Active</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((vendor) => (
                  <tr key={vendor.id}>
                    <td data-label="Company">
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        <div className="avatar">{initials(vendor.company_name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <Link to={`/manage-vendors/vendor/${vendor.id}`} className="strong">{vendor.company_name}</Link>
                          <span className="sub">{vendor.company_email} · {vendor.company_phone}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Contact person">
                      {vendor.representative_name}
                      <span className="sub">{vendor.representative_phone}</span>
                    </td>
                    <td data-label="Active"><Switch on={vendor.is_active} onClick={() => toggle(vendor)} label="Active" /></td>
                    <td className="actions-cell">
                      <button className="icon-btn" title="Edit" onClick={() => open('edit', vendor)}><FaEdit /></button>
                      <button className="icon-btn" title="Reset password" onClick={() => open('password', vendor)}><FaKey /></button>
                      <Link className="icon-btn" title="Open" to={`/manage-vendors/vendor/${vendor.id}`}><FaChevronRight /></Link>
                    </td>
                  </tr>
                )) : <EmptyRow cols={4}>{query ? 'No vendors match your search.' : 'No vendors yet.'}</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={!!mode}
        title={titles[mode]}
        subtitle={mode === 'password' && current ? `New panel password for ${current.company_name}.` : undefined}
        onClose={close}
        onSubmit={submit}
        wide={mode !== 'password'}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (progress > 0 && progress < 100 ? `Uploading ${progress}%` : 'Saving…') : 'Save'}
            </button>
          </>
        )}
      >
        <div className="form">
          <Alert>{error}</Alert>
          {mode === 'password' ? (
            <Field label="New password" help="At least 6 characters. Share it with the vendor securely.">
              <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={6} autoFocus />
            </Field>
          ) : (
            <>
              <div className="section-label">Company</div>
              <div className="form-grid">
                <Field label="Company name" full>
                  <input className="input" value={form.companyName} onChange={set('companyName')} required />
                </Field>
                <Field label="Email (used to sign in)">
                  <input className="input" type="email" value={form.companyEmail} onChange={set('companyEmail')} required />
                </Field>
                <Field label="Phone">
                  <input className="input" inputMode="tel" value={form.companyPhone} onChange={set('companyPhone')} required />
                </Field>
              </div>
              <hr className="divider" />
              <div className="section-label">Contact person</div>
              <div className="form-grid">
                <Field label="Name">
                  <input className="input" value={form.representativeName} onChange={set('representativeName')} required />
                </Field>
                <Field label="Phone">
                  <input className="input" inputMode="tel" value={form.representativePhone} onChange={set('representativePhone')} required />
                </Field>
              </div>
              {mode === 'add' && (
                <>
                  <hr className="divider" />
                  <div className="form-grid">
                    <Field label="Panel password" help="At least 6 characters.">
                      <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={6} />
                    </Field>
                    <Field label="Documents" help={form.documents.length ? `${form.documents.length} file(s) selected` : 'Registration, GST, ID — up to 5 files'}>
                      <label className="dropzone" style={{ padding: '10px 12px', flexDirection: 'row' }}>
                        <FaCloudUploadAlt /> <span><strong>Choose files</strong></span>
                        <input type="file" multiple accept="image/*,application/pdf" hidden
                          onChange={(e) => setForm({ ...form, documents: Array.from(e.target.files || []).slice(0, 5) })} />
                      </label>
                    </Field>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Modal>
    </Page>
  );
};

export default ManageVendors;
